import { ref, computed, Ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { PaginatedResponse, Patient } from '@/models';
import { useRouter } from 'vue-router';
import { IOption, ISuggestion } from '@/lib';
import { OrderItem, Variant, LensProperty, ProductImage } from '@/models/webshop.model';
import { useWebshopCartStore } from '@/stores/webshopCart.store';
import { useNotificationStore } from '@/stores/notification.store';
import debounce from 'lodash-es/debounce';
import { formatPower } from '@/lib/helpers/format-power';
import { OrganisationPatientService } from '@/services/api';
import { PRODUCT_CATEGORY_LENS_CARE } from '@/constants/webshop';
import { formatVariantLabel, sortVariantsForPackOptions } from '@/lib/helpers/variant-label.helper';

export interface PropertyOption {
  value: string;
  label: string;
}

export function useProductCard(props: {
  mlsId: string;
  productName: string;
  productCategory: string;
  productCategorySlug: string;
  lensProperties: LensProperty[];
  variants: Variant[];
  image?: ProductImage;
  lensCode?: number;
  powerSelectionMode?: 'single' | 'multi';
  dataDusk?: string;
  powerSelectionRef?: Ref<({ reset: () => void } | null)>;
  organisationId: Ref<string>;
}) {
  const { t } = useI18n();
  const webshopCartStore = useWebshopCartStore();
  const notificationStore = useNotificationStore();
  const router = useRouter();

  const radius = ref<string | null>(null);
  const diameter = ref<string | null>(null);
  const power = ref<string | null>(null);
  const cylinder = ref<string | null>(null);
  const axis = ref<string | null>(null);
  const pack = ref<string | null>(null);
  const quantity = ref<number>(1);
  const reference = ref<string>('');
  const patient = ref<Patient | null>(null);
  const patientSearchText = ref('');
  const patientSuggestions = ref<ISuggestion[]>([]);
  const isPatientSearchLoading = ref(false);

  const showPowerSelection = ref(false);
  const selectedPowerQuantities = ref<{ power: string; quantity: number }[]>([]);

  const isMultiPowerMode = computed(() => props.powerSelectionMode === 'multi');
  const isLensCare = computed(() => props.productCategory === PRODUCT_CATEGORY_LENS_CARE);

  const resetPatientState = () => {
    patient.value = null;
    patientSearchText.value = '';
    patientSuggestions.value = [];
  };
  watch(
    () => props.organisationId.value,
    () => {
      resetPatientState();
    }
  );

  const getNormalizedOptions = (name: string): IOption[] => {
    const property = props.lensProperties.find((p) => p.name === name);
    if (property) {
      if (property.options && Object.keys(property.options).length > 0) {
        return Object.entries(property.options)
          .map(([value, label]) => {
            const displayLabel = name === 'Power' ? formatPower(label as string) : (label as string);
            return { value, label: displayLabel };
          })
          .filter((opt) => opt.label !== '');
      }
      if ('stepMax' in property &&
          'stepMin' in property &&
          'stepValue' in property) {
        const options: IOption[] = [];
        const step = property.stepValue as number;
        const min = property.stepMin as number;
        const max = property.stepMax as number;
        for (let i = min; i <= max; i += step) {
          const raw = i.toFixed(2);
          const displayLabel = name === 'Power' ? formatPower(raw) : raw;
          options.push({ value: raw, label: displayLabel });
        }
        return options;
      }
    }
    return [];
  };

  const getPropertyOption = (name: string, value: string | null): PropertyOption | null => {
    if (!value) {
      return null;
    }

    const property = props.lensProperties.find((p) => p.name === name);
    if (property) {
      if (property.options && property.options[value]) {
        return {
          value,
          label: property.options[value] as string
        };
      }

      if ('stepMax' in property && 'stepMin' in property && 'stepValue' in property) {
        return {
          value,
          label: value
        };
      }
    }

    return null;
  };


  const getVariantByValue = (value: string | null): Variant | null => {
    if (!value || !props.variants) {
      return null;
    }

    return props.variants.find((p) => p.publicIdentifier === value) || null;
  };

  const radiusOptions = computed(() => getNormalizedOptions('Radius'));
  const diameterOptions = computed(() => getNormalizedOptions('Diameter'));
  const powerOptions = computed(() => {
    const options = getNormalizedOptions('Power');
    return options.sort((a, b) => {
      const av = parseFloat(a.label);
      const bv = parseFloat(b.label);
      const category = (v: number) => (v < 0 ? 0 : v === 0 ? 1 : 2);
      const ac = category(av);
      const bc = category(bv);
      if (ac !== bc) {
        return ac - bc;
      }
      if (ac === 0) {
        return Math.abs(av) - Math.abs(bv);
      }
      return av - bv;
    });
  });
  const cylinderOptions = computed(() => getNormalizedOptions('Cylinder'));
  const axisOptions = computed(() => getNormalizedOptions('Axis'));
  const packOptions = computed(() => {
    const variants = props.variants ?? [];
    const sorted = sortVariantsForPackOptions(variants);
    return sorted.map((variant) => ({
      label: formatVariantLabel(variant),
      value: variant.publicIdentifier
    }));
  });
  const validateReference = (event: { target: { value: string } }) => {
    if (event && event.target && typeof event.target.value === 'string') {
      reference.value = event.target.value.replace(/[<>"'&\n\r]/g, '').slice(0, 30);
    }
  };

  const searchPatients = async () => {
    if (patientSearchText.value.length < 1) {
      patientSuggestions.value = [];
      return;
    }
    isPatientSearchLoading.value = true;
    try {
      const currentPatientService = new OrganisationPatientService(props.organisationId.value);
      const response = await currentPatientService.index({
        params: {
          'filter[search]': patientSearchText.value,
          'page[size]': 10
        }
      });

      let patients: Patient[] = [];
      const responseData = response.data as any;

      if (responseData && typeof responseData === 'object' && 'data' in responseData && 'meta' in responseData && Array.isArray(responseData.data)) {
        patients = (responseData as PaginatedResponse<Patient[]>).data;
      } else if (Array.isArray(responseData)) {
        patients = responseData as Patient[];
      }

      patientSuggestions.value = patients.map((p: Patient) => ({
        id: p.id,
        value: p.id,
        label: `${p.first_name} ${p.last_name} - ID: ${p.mrn}` || `${p.first_name} ${p.last_name}`
      }));
    } catch (e) {
      console.error('Failed to search patients:', e);
      patientSuggestions.value = [];
    }
    isPatientSearchLoading.value = false;
  };

  const debouncedPatientSearch = debounce(searchPatients, 500);
  const isPatientSetting = ref(false);

  const handlePatientChange = async (suggestion: ISuggestion | string) => {
    isPatientSetting.value = true;
    if (typeof suggestion === 'object' && suggestion !== null && 'value' in suggestion && 'label' in suggestion && suggestion.id) {
      try {
        const currentOrganisationId = props.organisationId.value;

        if (currentOrganisationId) {
          const patientService = new OrganisationPatientService(currentOrganisationId);
          try {
            const patientData = await patientService.fetch(suggestion.id as string);
            patient.value = patientData;
          } catch (fetchError) {
            console.error('Error fetching patient details:', fetchError);
            patient.value = {
              id: suggestion.id as string,
              first_name: suggestion.label.split(' ')[0] || '',
              last_name: suggestion.label.split(' ').slice(1).join(' ') || '',
              date_of_birth: '',
              age: 0
            };
          }
        } else {
          patient.value = {
            id: suggestion.id as string,
            first_name: suggestion.label.split(' ')[0] || '',
            last_name: suggestion.label.split(' ').slice(1).join(' ') || '',
            date_of_birth: '',
            age: 0
          };
        }
      } catch (error) {
        console.error('Failed to process patient selection:', error);
        patient.value = {
          id: suggestion.id as string,
          first_name: suggestion.label.split(' ')[0] || '',
          last_name: suggestion.label.split(' ').slice(1).join(' ') || '',
          date_of_birth: '',
          age: 0
        };
      }

      patientSearchText.value = suggestion.label;
      patientSuggestions.value = [];
      isPatientSetting.value = false;
    } else if (typeof suggestion === 'string') {
      patientSearchText.value = suggestion;
      if (suggestion === '') {
        patient.value = null;
        patientSuggestions.value = [];
        isPatientSetting.value = false;
      } else {
        debouncedPatientSearch();
        isPatientSetting.value = false;
      }
    }
  };

  const isProcessing = ref(false);
  const handleAddToCart = async () => {
    if (isProcessing.value) {
      return;
    }
    isProcessing.value = true;
    let orderItem: OrderItem;
    const variantInfo = getVariantByValue(pack.value);
    const baseProductOptions = {
      category: {
        name: props.productCategory,
        slug: props.productCategorySlug
      },
      variants: props.variants,
      powers: props.lensProperties?.find((p: LensProperty) => p.name === 'Power') || null,
      ...((!isLensCare.value && props.lensCode) && { lensCode: props.lensCode })
    };

    const productOptions = props.image ? { ...baseProductOptions, image: props.image } : baseProductOptions;

    if (isMultiPowerMode.value) {
      orderItem = {
        mlsId: props.mlsId,
        patientId: patient.value?.id || null,
        productName: props.productName,
        options: JSON.stringify({
          Radius: getPropertyOption('Radius', radius.value),
          Diameter: getPropertyOption('Diameter', diameter.value),
          Cylinder: getPropertyOption('Cylinder', cylinder.value),
          Axis: getPropertyOption('Axis', axis.value)
        }),
        reference: reference.value || null,
        quantity: quantity.value,
        variantOptionValue: variantInfo?.publicIdentifier || '',
        variantOptionLabel: formatVariantLabel(variantInfo),
        productOptions,
        isStock: true,
        powerQuantities: JSON.stringify(
          selectedPowerQuantities.value.map((power) => ({
            power: power.power,
            quantity: power.quantity
          }))
        )
      };
    } else if (isLensCare.value) {
      orderItem = {
        mlsId: props.mlsId,
        patientId: patient.value?.id || null,
        productName: props.productName,
        options: JSON.stringify({}),
        reference: reference.value || null,
        quantity: quantity.value,
        variantOptionValue: variantInfo?.publicIdentifier || '',
        variantOptionLabel: formatVariantLabel(variantInfo),
        productOptions
      };
    } else {
      orderItem = {
        mlsId: props.mlsId,
        patientId: patient.value?.id || null,
        productName: props.productName,
        options: JSON.stringify({
          Radius: getPropertyOption('Radius', radius.value),
          Diameter: getPropertyOption('Diameter', diameter.value),
          Power: getPropertyOption('Power', power.value),
          Cylinder: getPropertyOption('Cylinder', cylinder.value),
          Axis: getPropertyOption('Axis', axis.value)
        }),
        reference: reference.value || null,
        quantity: quantity.value,
        variantOptionValue: variantInfo?.publicIdentifier || '',
        variantOptionLabel: formatVariantLabel(variantInfo),
        productOptions
      };
    }

    try {
      await webshopCartStore.addItem(orderItem);
      notificationStore.addSuccessNotification({
        title: t('platform.common.success'),
        label: t('platform.webshop.notifications.item-added'),
        action: {
          label: t('platform.webshop.button.view-cart'),
          onClick: () => {
            router.push({ name: 'webshop-cart' });
          }
        },
        autoDismiss: true,
        dismissAfter: 3000
      });
      resetFormFields();
    } catch (error) {
      notificationStore.addErrorNotification({
        title: t('platform.common.failure'),
        label: t('platform.webshop.notifications.item-add-failed')
      });
    } finally {
      isProcessing.value = false;
    }
  };

  const resetFormFields = (): void => {
    patient.value = null;
    patientSearchText.value = '';
    patientSuggestions.value = [];

    if (isLensCare.value && !isMultiPowerMode.value) {
      pack.value = null;
      quantity.value = 1;
      reference.value = '';
      return;
    }

    radius.value = null;
    diameter.value = null;
    power.value = null;
    cylinder.value = null;
    axis.value = null;
    pack.value = null;
    quantity.value = 1;
    reference.value = '';

    if (isMultiPowerMode.value) {
      selectedPowerQuantities.value = [];
      showPowerSelection.value = false;
      props.powerSelectionRef?.value?.reset();
    }

  };

  const isAddToCartDisabled = computed(() => {
    const isInvalidQuantity = quantity.value < 1;
    if (isProcessing.value) {
      return true;
    }

    if (isMultiPowerMode.value) {
      const requiredParams = [
        { param: radius, options: radiusOptions.value },
        { param: diameter, options: diameterOptions.value },
        { param: cylinder, options: cylinderOptions.value },
        { param: axis, options: axisOptions.value },
        { param: pack, options: packOptions.value }
      ];

      return (
        requiredParams.some(({ param, options }) => options.length > 0 && !param.value) ||
        !selectedPowerQuantities.value.some((power) => power.quantity > 0)
      );
    }

    if (isLensCare.value) {
      const hasPackOptions = packOptions.value.length > 0;
      return (hasPackOptions && !pack.value) || isInvalidQuantity;
    }

    const requiredParams = [
      { param: radius, options: radiusOptions.value },
      { param: diameter, options: diameterOptions.value },
      { param: power, options: powerOptions.value },
      { param: cylinder, options: cylinderOptions.value },
      { param: axis, options: axisOptions.value },
      { param: pack, options: packOptions.value }
    ];

    return (
      isInvalidQuantity ||
      requiredParams.some(({ param, options }) => options.length > 0 && !param.value)
    );
  });
  watch([radius, diameter, cylinder, axis, pack], ([newRadius, newDiameter, newCylinder, newAxis, newPack]) => {
    if (props.powerSelectionMode === 'multi' && (newRadius || newDiameter || newCylinder || newAxis || newPack)) {
      showPowerSelection.value = true;
    }
  }, { immediate: true });

  return {
    radius,
    diameter,
    power,
    cylinder,
    axis,
    pack,
    quantity,
    reference,
    patient,
    patientSearchText,
    patientSuggestions,
    isPatientSearchLoading,
    isPatientSetting,
    showPowerSelection,
    selectedPowerQuantities,
    radiusOptions,
    diameterOptions,
    powerOptions,
    cylinderOptions,
    axisOptions,
    packOptions,
    isMultiPowerMode,
    isLensCare,
    isAddToCartDisabled,
    getPropertyOption,
    getVariantByValue,
    handleAddToCart,
    resetFormFields,
    validateReference,
    handlePatientChange,
    searchPatients,
    debouncedPatientSearch
  };
}
